/**
 * JIRA: SD-13645
 * Normalizes border WIDTHS between the resting and hover theme variables, so hovering never
 * changes an element's size while each state keeps its own colour and line style.
 *
 * Covers the four button families and the two carousel surfaces - the navigation arrows and
 * the card itself. The carousel pair was added once those variables shipped: a theme setting
 * carouselItemBorder to `solid 1px` and carouselItemBorderHover to `solid 2px` makes every
 * card grow 2px under the pointer, which is the same defect the button families had.
 *
 * Themes define borders as two independent shorthands per element (--xBorder and
 * --xBorderHover). CSS cannot take the width from one and the colour from the other -
 * a shorthand is used whole - so whenever a theme pairs different widths the button physically
 * grows or shrinks on hover. Measured on live bots: the Giants wide theme pairs `solid 1px`
 * with `solid 2px` on all four families (every button grew 2px on hover) and the dark theme
 * pairs `solid 1px` with `solid 0px` on link buttons (the border vanished and the button
 * shrank). Fixing each theme by hand works but has to be re-done for every client; this runs
 * once per page against whatever the theme delivered.
 *
 * Rule: when both variables parse and their widths differ, both are rewritten inline on the
 * root element (inline wins over the `:root` stylesheet rule) using the LARGER width, keeping
 * each state's own style and colour. A hover variable with no base counterpart gets a
 * transparent base of the same width, so a border can never appear from nothing. Everything
 * else - unset pairs, equal widths, unparseable values - is deliberately left alone, and the
 * CSS fallback chains in buttons.css keep covering those cases.
 */

/**
 * Base variable names. Each is paired with its "Hover" counterpart at read time, so adding a
 * surface here is the only change needed to bring it under the same rule.
 */
const BORDER_VARIABLES = [
	"--buttonPrimaryBorder",
	"--buttonSecondaryBorder",
	"--buttonLinkBorder",
	"--buttonQuickReplyBorder",
	"--carouselArrowBorder",
	"--carouselItemBorder",
] as const;

const BORDER_STYLES = new Set([
	"none",
	"hidden",
	"solid",
	"dashed",
	"dotted",
	"double",
	"groove",
	"ridge",
	"inset",
	"outset",
]);

interface IParsedBorder {
	/** Width in px. Only px (and the bare `0`) parse - other units abort normalization. */
	width: number;
	style: string;
	color: string;
}

/**
 * Parses a border shorthand ("solid 1px #8A8A8A", "1px solid rgb(0, 0, 0)", "0", ...).
 * Token order is free, matching how the dashboard themes write them. Returns null when the
 * value is empty or uses a width unit other than px, in which case the caller must not touch
 * that family.
 */
export const parseBorderShorthand = (value: string): IParsedBorder | null => {
	const trimmed = (value || "").trim();
	if (!trimmed) return null;

	// Colour functions may contain spaces ("rgb(0, 0, 0)") - keep them as one token.
	const tokens = trimmed.match(/(?:[a-zA-Z-]+\([^)]*\)|\S+)/g) || [];

	let width: number | null = null;
	let style = "";
	const colorTokens: string[] = [];

	for (const token of tokens) {
		const widthMatch = /^(-?\d*\.?\d+)(px)?$/.exec(token);

		if (widthMatch && width === null) {
			width = parseFloat(widthMatch[1]);
			continue;
		}

		if (width === null && /^-?\d*\.?\d+(em|rem|pt|%|vw|vh)$/.test(token)) {
			// A width in a unit this helper does not convert - bail out for this family.
			return null;
		}

		if (!style && BORDER_STYLES.has(token.toLowerCase())) {
			style = token.toLowerCase();
			continue;
		}

		colorTokens.push(token);
	}

	return {
		width: width === null ? 0 : width,
		style,
		color: colorTokens.join(" "),
	};
};

const composeBorder = (border: IParsedBorder, width: number): string => {
	// A widened border needs a visible line style; a bare `0` shorthand has none.
	const style = border.style && border.style !== "none" ? border.style : "solid";
	const color = border.color || "transparent";

	return `${style} ${width}px ${color}`;
};

/**
 * Reads every border pair above and rewrites any width-mismatched pair inline.
 * `readVariable`/`writeVariable` default to the document root and exist so tests can supply
 * their own store instead of relying on jsdom's partial custom-property support.
 */
export const normalizeHoverBorders = (
	readVariable: (name: string) => string = (name) =>
		getComputedStyle(document.documentElement).getPropertyValue(name),
	writeVariable: (name: string, value: string) => void = (name, value) =>
		document.documentElement.style.setProperty(name, value)
): void => {
	BORDER_VARIABLES.forEach((baseName) => {
		const hoverName = `${baseName}Hover`;

		const baseRaw = readVariable(baseName).trim();
		const hoverRaw = readVariable(hoverName).trim();

		// No hover value: the CSS fallback chain already reuses the base border - no jump.
		if (!hoverRaw) return;

		const hover = parseBorderShorthand(hoverRaw);
		// A value this helper cannot read reliably (e.g. an em width): leave the pair alone
		// rather than rewrite it wrongly.
		if (!hover) return;

		// Hover defined with no base: without a reserved width the border would appear from
		// nothing and grow the button. Reserve the space invisibly.
		if (!baseRaw) {
			if (hover.width > 0) {
				writeVariable(baseName, composeBorder({ width: 0, style: hover.style, color: "transparent" }, hover.width));
			}
			return;
		}

		const base = parseBorderShorthand(baseRaw);
		if (!base) return;

		if (base.width === hover.width) return;

		const width = Math.max(base.width, hover.width);

		// Only the narrower side changes; rewriting the side already at the max would just
		// shadow the theme's own value with a reformatted copy.
		if (base.width !== width) writeVariable(baseName, composeBorder(base, width));
		if (hover.width !== width) writeVariable(hoverName, composeBorder(hover, width));
	});
};
